import { useMemo, useState } from 'react';
import { AlertTriangle, Calculator, CheckCircle2, Download, FileText, Leaf, Package, Plus, ShoppingCart, Trash2, X } from 'lucide-react';

const optionLabel = (item) => `${item.name}${item.category ? ` (${item.category})` : ''}`;

export default function AdminPlanningTab({
  capacity,
  planItems,
  library,
  ingredients,
  updatePlanRow,
  removePlanRow,
  addPlanRow,
  runPlan,
  planResult,
  shoppingList,
  addShoppingItem,
  updateShoppingItem,
  removeShoppingItem,
  clearShoppingList,
  downloadPlanningCSV,
  downloadPlanningPDF,
}) {
  const [manualItem, setManualItem] = useState({ name: '', qty: '', unit: 'pieces' });
  const [inventoryPick, setInventoryPick] = useState('');

  const shortageRows = useMemo(() => planResult?.shopping_summary || [], [planResult]);
  const shortageCount = shortageRows.filter(r => r.to_buy > 0).length;
  const plannedServings = planItems.reduce((sum, row) => sum + (parseInt(row.desired_servings, 10) || 0), 0);
  const addManual = () => {
    if (!manualItem.name.trim()) return;
    addShoppingItem({ name: manualItem.name.trim(), qty: parseFloat(manualItem.qty) || 1, unit: manualItem.unit, source: 'ad hoc' });
    setManualItem({ name: '', qty: '', unit: 'pieces' });
  };
  const addInventoryItem = () => {
    const ingredient = ingredients.find(i => i.id === inventoryPick);
    if (!ingredient) return;
    addShoppingItem({ name: ingredient.name, qty: Math.max(1, ingredient.low_threshold - ingredient.stock_qty), unit: ingredient.unit || 'pieces', source: 'inventory', ingredient_id: ingredient.id });
    setInventoryPick('');
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="admin-planning-section">
      <PlanningOverview planCount={planItems.length} plannedServings={plannedServings} shortageCount={shortageCount} shoppingCount={shoppingList.length} />
      <PlannerBuilder planItems={planItems} library={library} updatePlanRow={updatePlanRow} removePlanRow={removePlanRow} addPlanRow={addPlanRow} runPlan={runPlan} />
      <IngredientNeedsTable rows={shortageRows} />
      <ShoppingListEditor
        shoppingList={shoppingList}
        manualItem={manualItem}
        setManualItem={setManualItem}
        addManual={addManual}
        ingredients={ingredients}
        inventoryPick={inventoryPick}
        setInventoryPick={setInventoryPick}
        addInventoryItem={addInventoryItem}
        updateShoppingItem={updateShoppingItem}
        removeShoppingItem={removeShoppingItem}
        clearShoppingList={clearShoppingList}
        downloadPlanningCSV={downloadPlanningCSV}
        downloadPlanningPDF={downloadPlanningPDF}
      />
      <CapacityView capacity={capacity} />
    </div>
  );
}

function PlanningOverview({ planCount, plannedServings, shortageCount, shoppingCount }) {
  const stats = [
    { label: 'Plan rows', value: planCount, testId: 'planning-overview-plan-count' },
    { label: 'Servings', value: plannedServings, testId: 'planning-overview-serving-count' },
    { label: 'Shortages', value: shortageCount, testId: 'planning-overview-shortage-count' },
    { label: 'Shopping', value: shoppingCount, testId: 'planning-overview-shopping-count' },
  ];

  return (
    <section className="relative overflow-hidden rounded-3xl bg-[#1B3A1A] text-white shadow-[0_18px_45px_rgba(27,58,26,0.18)]" data-testid="planning-overview-panel">
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#B8860B]/20 to-transparent" />
      <div className="relative grid gap-6 p-6 sm:p-7 lg:grid-cols-[1.25fr_1fr] lg:items-end">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3F0E6]" data-testid="planning-overview-kicker">
            <Leaf size={12} /> Prep Forecast
          </div>
          <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">Plan service, spot shortages, shop once.</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#F3F0E6]/85">
            Build the serving plan, calculate ingredient needs, and keep the grocery list synced as the numbers change.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {stats.map(stat => (
            <div key={stat.testId} className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm" data-testid={stat.testId}>
              <div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#D4CFC5]">{stat.label}</div>
              <div className="mt-1 font-mono text-2xl font-bold text-white">{stat.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlannerBuilder({ planItems, library, updatePlanRow, removePlanRow, addPlanRow, runPlan }) {
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="inventory-planning">
      <SectionHeader
        icon={<Calculator size={18} />}
        eyebrow="Step 1"
        title="Build the serving plan"
        description="Choose menu items and target quantities for the next service."
        testId="planning-builder-header"
      />
      <div className="mt-5 space-y-3">
        {planItems.map((p, idx) => (
          <div key={p.row_id} className="grid grid-cols-1 items-center gap-3 rounded-2xl border border-transparent bg-[#F9F6F0] p-3 transition-all duration-200 hover:border-[#7D8B69]/20 hover:bg-[#F3F0E6] sm:grid-cols-[1fr_150px_44px]" data-testid={`planning-row-${idx}`}>
            <select value={p.library_item_id} onChange={e => updatePlanRow(idx, 'library_item_id', e.target.value)} className="h-12 rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid={`plan-item-${idx}`}>
              <option value="">Select item...</option>
              {library.items.map(li => <option key={li.id} value={li.id} label={optionLabel(li)} />)}
            </select>
            <input type="number" min="0" value={p.desired_servings} onChange={e => updatePlanRow(idx, 'desired_servings', parseInt(e.target.value) || 0)} className="h-12 rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-right font-mono text-base font-bold transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" placeholder="Servings" data-testid={`plan-qty-${idx}`} />
            <button onClick={() => removePlanRow(idx)} className="flex h-11 w-11 items-center justify-center rounded-full text-[#C43030] transition-all duration-200 hover:bg-[#FFF3F3] active:scale-95" data-testid={`remove-plan-row-${idx}`} aria-label="Remove plan row"><X size={18} /></button>
          </div>
        ))}
        {planItems.length === 0 && <div className="rounded-2xl border border-dashed border-[#7D8B69]/25 bg-[#FDFBF7] p-6 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid="planning-empty-plan-message">Add items to begin planning</div>}
        <div className="flex flex-col gap-3 sm:flex-row">
          <button onClick={addPlanRow} className="h-12 flex-1 rounded-xl border border-dashed border-[#7D8B69]/35 bg-[#FDFBF7] font-mono text-xs font-bold uppercase tracking-wider text-[#4A5D4E] transition-all duration-200 hover:bg-[#F3F0E6] active:scale-[0.99]" data-testid="add-plan-row"><Plus size={14} className="inline" /> Add Plan Item</button>
          <button onClick={runPlan} disabled={planItems.length === 0} className="h-12 rounded-xl bg-[#1B3A1A] px-6 font-mono text-xs font-bold uppercase tracking-wider text-white shadow-sm transition-all duration-200 hover:bg-[#2C4C2B] active:scale-[0.98] disabled:opacity-40" data-testid="run-plan-btn"><Calculator size={14} className="inline mr-1" /> Calculate Needs</button>
        </div>
      </div>
    </section>
  );
}

function IngredientNeedsTable({ rows }) {
  const shortageCount = rows.filter(r => r.to_buy > 0).length;
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="planning-ingredient-table">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <SectionHeader
          icon={<AlertTriangle size={18} />}
          eyebrow="Step 2"
          title="Ingredient needs"
          description="Review what is on hand, what is needed, and what is short."
          testId="planning-needs-header"
        />
        <span className={`inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider ${shortageCount > 0 ? 'border border-[#F4B4B4] bg-[#FFF3F3] text-[#C43030]' : 'border border-[#C8DFC0] bg-[#E8F0E4] text-[#1B3A1A]'}`} data-testid="shortages-auto-sync-status">
          {shortageCount > 0 ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
          {shortageCount > 0 ? `${shortageCount} shortage${shortageCount === 1 ? '' : 's'} synced to shopping list` : 'Shortages sync automatically'}
        </span>
      </div>
      <div className="mt-5 overflow-hidden rounded-2xl bg-[#FDFBF7]">
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="planning-ingredients-table">
          <thead className="font-mono text-[10px] uppercase tracking-wider text-[#7D8B69]">
            <tr className="border-b border-[#7D8B69]/20"><th className="text-left p-3">Ingredient</th><th className="text-right p-3">Available</th><th className="text-right p-3">Needed</th><th className="text-right p-3">Short</th><th className="text-left p-3">Unit</th></tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => <tr key={`${row.ingredient_name}-${idx}`} className="border-b border-[#7D8B69]/10 transition-colors duration-200 hover:bg-[#F3F0E6]" data-testid={`planning-ingredient-row-${idx}`}><td className="p-3 font-heading font-bold text-[#1B3A1A]">{row.ingredient_name}</td><td className="p-3 text-right font-mono">{row.on_hand}</td><td className="p-3 text-right font-mono">{row.total_needed}</td><td className="p-3 text-right"><span className={`inline-flex min-w-12 justify-center rounded-full px-3 py-1 font-mono text-xs font-bold ${row.to_buy > 0 ? 'bg-[#FFF3F3] text-[#C43030]' : 'bg-[#E8F0E4] text-[#1B3A1A]'}`}>{row.to_buy}</span></td><td className="p-3 font-mono text-[#7D8B69]">{row.unit}</td></tr>)}
            {rows.length === 0 && <tr><td colSpan="5" className="p-8 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]">Run a plan to see ingredient availability and shortages</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
}

function ShoppingListEditor({ shoppingList, manualItem, setManualItem, addManual, ingredients, inventoryPick, setInventoryPick, addInventoryItem, updateShoppingItem, removeShoppingItem, clearShoppingList, downloadPlanningCSV, downloadPlanningPDF }) {
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="editable-shopping-list">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <SectionHeader
          icon={<ShoppingCart size={18} />}
          eyebrow="Step 3"
          title="Saved shopping list"
          description="Shortages sync from Planning. Add one-off items, check off purchases, then export."
          testId="planning-shopping-header"
        />
        <div className="flex gap-2">
          <button onClick={downloadPlanningCSV} className="h-11 rounded-xl border border-[#7D8B69]/25 px-3 font-mono text-[10px] font-bold uppercase text-[#1B3A1A] transition-all duration-200 hover:bg-[#F3F0E6] active:scale-95" data-testid="planning-download-csv-btn"><Download size={12} className="inline mr-1" />CSV</button>
          <button onClick={downloadPlanningPDF} className="h-11 rounded-xl border border-[#7D8B69]/25 px-3 font-mono text-[10px] font-bold uppercase text-[#1B3A1A] transition-all duration-200 hover:bg-[#F3F0E6] active:scale-95" data-testid="planning-download-pdf-btn"><FileText size={12} className="inline mr-1" />PDF</button>
          <button onClick={clearShoppingList} className="h-11 rounded-xl border border-[#F4B4B4] px-3 font-mono text-[10px] font-bold uppercase text-[#C43030] transition-all duration-200 hover:bg-[#FFF3F3] active:scale-95" data-testid="clear-shopping-list-btn">Clear</button>
        </div>
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_0.9fr]">
        <div className="rounded-2xl bg-[#FDFBF7] p-4" data-testid="adhoc-shopping-form">
          <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">Add ad hoc item</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_90px_110px_90px]">
            <input value={manualItem.name} onChange={e => setManualItem(i => ({ ...i, name: e.target.value }))} placeholder="Item name" className="h-12 rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="shopping-adhoc-name-input" />
            <input type="number" min="0" value={manualItem.qty} onChange={e => setManualItem(i => ({ ...i, qty: e.target.value }))} placeholder="Qty" className="h-12 rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-right text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="shopping-adhoc-qty-input" />
            <input value={manualItem.unit} onChange={e => setManualItem(i => ({ ...i, unit: e.target.value }))} placeholder="Unit" className="h-12 rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="shopping-adhoc-unit-input" />
            <button onClick={addManual} className="h-12 rounded-xl bg-[#4A7C3F] px-4 font-mono text-xs font-bold uppercase text-white transition-all duration-200 hover:bg-[#3D6B34] active:scale-[0.98]" data-testid="shopping-add-adhoc-btn"><Plus size={14} className="inline" /> Add</button>
          </div>
        </div>
        <div className="rounded-2xl bg-[#FDFBF7] p-4">
          <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">Add from inventory</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_135px]">
            <select value={inventoryPick} onChange={e => setInventoryPick(e.target.value)} className="h-12 rounded-xl border border-[#7D8B69]/25 bg-white px-4 text-sm transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid="shopping-inventory-select"><option value="">Choose inventory item...</option>{ingredients.map(i => <option key={i.id} value={i.id} label={`${i.name} (${i.stock_qty} ${i.unit})`} />)}</select>
            <button onClick={addInventoryItem} className="h-12 rounded-xl border border-[#4A7C3F] px-4 font-mono text-xs font-bold uppercase text-[#4A7C3F] transition-all duration-200 hover:bg-[#4A7C3F] hover:text-white active:scale-[0.98]" data-testid="shopping-add-inventory-btn">Add Inventory</button>
          </div>
        </div>
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl bg-[#FDFBF7]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="shopping-list-table">
            <thead className="font-mono text-[10px] uppercase tracking-wider text-[#7D8B69]"><tr className="border-b border-[#7D8B69]/20"><th className="text-left p-3">Done</th><th className="text-left p-3">Item</th><th className="text-right p-3">Qty</th><th className="text-left p-3">Unit</th><th className="text-left p-3">Source</th><th className="p-3" /></tr></thead>
            <tbody>
              {shoppingList.map(item => <ShoppingRow key={item.id} item={item} updateShoppingItem={updateShoppingItem} removeShoppingItem={removeShoppingItem} />)}
              {shoppingList.length === 0 && <tr><td colSpan="6" className="p-8 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]">No shopping items yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ShoppingRow({ item, updateShoppingItem, removeShoppingItem }) {
  return (
    <tr className="border-b border-[#7D8B69]/10 transition-colors duration-200 hover:bg-[#F3F0E6]" data-testid={`shopping-list-row-${item.id}`}>
      <td className="p-3"><input type="checkbox" checked={!!item.checked} onChange={e => updateShoppingItem(item.id, { checked: e.target.checked })} className="h-5 w-5 cursor-pointer accent-[#1B3A1A]" data-testid={`shopping-list-check-${item.id}`} /></td>
      <td className="p-3"><input value={item.name} onChange={e => updateShoppingItem(item.id, { name: e.target.value })} className="h-10 w-full rounded-xl border border-[#7D8B69]/20 bg-white px-3 transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid={`shopping-list-name-${item.id}`} /></td>
      <td className="p-3"><input type="number" min="0" value={item.qty} onChange={e => updateShoppingItem(item.id, { qty: parseFloat(e.target.value) || 0 })} className="h-10 w-24 rounded-xl border border-[#7D8B69]/20 bg-white px-3 text-right font-mono transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid={`shopping-list-qty-${item.id}`} /></td>
      <td className="p-3"><input value={item.unit} onChange={e => updateShoppingItem(item.id, { unit: e.target.value })} className="h-10 w-28 rounded-xl border border-[#7D8B69]/20 bg-white px-3 transition-all duration-200 focus:border-[#1B3A1A] focus:ring-2 focus:ring-[#1B3A1A]/10" data-testid={`shopping-list-unit-${item.id}`} /></td>
      <td className="p-3"><span className="rounded-full bg-white px-3 py-1 font-mono text-[10px] font-bold uppercase text-[#7D8B69]">{item.source}</span></td>
      <td className="p-3 text-right"><button onClick={() => removeShoppingItem(item.id)} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#C43030] transition-all duration-200 hover:bg-[#FFF3F3] active:scale-95" data-testid={`shopping-list-remove-${item.id}`} aria-label="Remove shopping item"><Trash2 size={14} /></button></td>
    </tr>
  );
}

function CapacityView({ capacity }) {
  if (capacity.length === 0) return null;
  return (
    <section className="rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)] sm:p-6" data-testid="capacity-view">
      <SectionHeader
        icon={<Package size={18} />}
        eyebrow="Inventory capacity"
        title="Max servings from current stock"
        description="Use this as a quick read on what the kitchen can support today."
        testId="planning-capacity-header"
      />
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {capacity.map(c => <div key={c.id} className="rounded-2xl bg-[#FDFBF7] p-4 transition-all duration-200 hover:bg-[#F3F0E6]"><div className="font-heading text-sm font-bold text-[#1B3A1A]">{c.name}</div><div className={`mt-2 font-mono text-lg font-bold ${c.max_servings === 0 ? 'text-[#C43030]' : c.no_recipe ? 'text-[#B8B0A0]' : 'text-[#4A7C3F]'}`}>{c.no_recipe ? 'No recipe' : `${c.max_servings} servings`}</div>{c.limiting_ingredient && <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#7D8B69]">Limited by {c.limiting_ingredient}</div>}</div>)}
      </div>
    </section>
  );
}

function SectionHeader({ icon, eyebrow, title, description, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#E8F0E4] text-[#1B3A1A]">{icon}</div>
      <div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#7D8B69]">{eyebrow}</div>
        <h3 className="mt-1 font-heading text-xl font-bold leading-tight text-[#1B3A1A]">{title}</h3>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-[#4A5D4E]">{description}</p>
      </div>
    </div>
  );
}
